<template>
    <div class="app-public-review-generate">
        <div class="app-public-review-generate__header page-main__section-header">
            <h3 v-if="enableTitle" class="app-public-review-generate__header-title page-main__section-title">Review Links</h3>
            <template v-if="!isLiteMode">
                <b-dropdown v-if="hasLocales" split @click="createLink(publicationData.id)" :text="isLinkCreating ? 'Creating...' : 'Create New Review Link'" class="element-button--secondary-dropdown-captions element-button--secondary-dropdown">
                    <b-dropdown-item v-for="(locale, index) in locales"
                        :key="`locale-${index}`"
                        @click="createLink(publicationData.id, locale.locale)"
                        >Create {{ locale.language_code }}  translation review link
                    </b-dropdown-item>
                </b-dropdown>
                <app-button v-else theme="secondary" @click="createLink(publicationData.id)">
                    {{ isLinkCreating ? 'Creating...' : 'Create New Review Link' }}
                </app-button>
            </template>
        </div>
        <div v-if="publicationData.links && publicationData.links.length" class="app-public-review-generate__body">
            <div v-for="link in publicationData.links" :key="link.code" class="app-public-review-generate__link grid">
                <div class="app-public-review-generate__link-header col-12">
                    <div v-if="link.is_locked == 2" class="element-featured-icon is--sm">
                        <svg title="This link is locked and will not show future changes" class="element-icon--lock element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-lock"></use></svg>
                    </div>
                    <h4 v-show="!isEditingName[link.id]" class="app-public-review-generate__link-title">
                        <span class="app-public-review-generate__link-label">Name</span>
                        {{ link.name }}
                    </h4>

                    <div v-show="isEditingName[link.id]" class="app-public-review-generate__link-name-edit">
                        <div class="input text">
                            <label for="previewconfiguration">Name</label>
                            <input type="text" name="previewname" placeholder="i.e. Alpha Version" required="required"
                                :value="link.name" :ref="'link-id-' + link.id">
                        </div>
                        <button class="element-button element-button--ghost" @click="updatePreviewLinkName(link.id, $event)">Save</button>
                        <p v-if="isLinkNameSaving[link.id]">Saving...</p>
                    </div>

                    <app-button theme="icon" class="app-public-review-generate__link-edit name-and-locale" title="Edit Link Name"
                        @click="isEditingName[link.id] = !isEditingName[link.id]">
                        <svg v-if="!isEditingName[link.id]" class="element-icon--edit element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-edit"></use></svg>
                        <svg v-else class="element-icon--x element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-x"></use></svg>
                    </app-button>

                    <div class="dropdown">
                        <button class="element-button--icon element-button" type="button" data-bs-toggle="dropdown">
                            <svg class="element-icon--more-info element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-more-info"></use></svg>
                        </button>
                        <div class="app-dropdown dropdown-menu">
                            <button v-if="link.is_locked != 2" theme="secondary" class="app-public-review-generate__link-reset-expiration" title="Reset Expiration"
                            @click="resetLink(link.id)">
                                <svg aria-hidden="true" class="element-icon--refresh element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-refresh"></use></svg>
                                {{ isLinkReseting[link.id] ? "Resetting..." : "Reset Expiration" }}
                            </button>
                            <button @click="lockLink(link.id, link.is_locked)">
                                <svg class="element-icon--lock element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-lock"></use></svg>
                                <span v-if="link.is_locked == 0" title="Lock">Lock</span>
                                <span v-else-if="link.is_locked == 1" title="Lock">Locking</span>
                                <span v-else-if="link.is_locked == 2" title="Unlock">Unlock</span>
                                <span v-else title="Unlock">Unlocking</span>
                            </button>
                            <button class="is--alert" title="Delete Link"
                                @click="removeLink(link.id)">
                                <svg aria-hidden="true" class="element-icon--delete element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-delete"></use></svg>
                                Delete
                            </button>
                        </div>
                    </div>

                    <div v-if="link.locale" class="element-badge is--alt"
                        :title="getLanguageLabel(link.locale)">
                        {{ getLanguageLabel(link.locale) }}
                    </div>
                </div>

                <div class="app-public-review-generate__link-url col-12" v-if="link.is_locked != 2">
                    <p class="app-public-review-generate__link-label">URL</p>
                    <a :href="link.url" target="_blank" class="app-public-review-generate__link-url-link is--alt">{{ link.url }}</a>
                    <app-button class="app-public-review-generate__link-url-copy element-button--icon element-button" title="Copy Link"
                        @click="copyLink(link)">
                        <svg class="element-icon--link element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-link"></use></svg>
                        <!-- {{ isLinkCopying[link.id] ? "Copying" : "Copy Link" }} -->
                    </app-button>
                </div>

                <div class="app-public-review-generate__link-url col-12 col-md-6" v-if="link.is_locked == 2">
                    <p class="app-public-review-generate__link-label">URL</p>
                    <a v-if="link.is_locked == 2" :href="link.locked_link_url" target="_blank" class="app-public-review-generate__link-url-link is--alt">{{ link.locked_link_url }}</a>
                    <app-button class="app-public-review-generate__link-url-copy element-button--icon element-button" title="Copy Link"
                        @click="copyLink(link, link.locked_link_url)">
                        <svg class="element-icon--link element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-link"></use></svg>
                        <!-- {{ isLinkCopying[link.id] ? "Copying" : "Copy Link" }} -->
                    </app-button>
                </div>
                <div class="app-public-review-generate__link-url col-12 col-md-6" v-if="link.is_locked == 2">
                    <p class="app-public-review-generate__link-label">SCORM</p>
                    <a v-if="link.is_locked == 2" :href="link.locked_zip_url" target="_blank" class="app-public-review-generate__link-url-link is--alt">{{ link.locked_zip_url }}</a>
                    <app-button class="app-public-review-generate__link-url-copy element-button--icon element-button" title="Copy Link"
                        @click="copyLink(link, link.locked_zip_url)">
                        <svg class="element-icon--link element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-link"></use></svg>
                        <!-- {{ isLinkCopying[link.id] ? "Copying" : "Copy Link" }} -->
                    </app-button>
                </div>

                <hr class="col-12"/>

                <p class="app-public-review-generate__link-metric col-12"><span class="app-public-review-generate__link-label">Created Date</span> {{ formatTime(link.created_at) }}</p>
                <template v-if="link.is_locked != 2">
                <p class="app-public-review-generate__link-metric col-12"><span class="app-public-review-generate__link-label">Expiration Date</span> {{ formatTime(link.expire_at) }}</p>
                </template>
                <template v-else>
                <p class="app-public-review-generate__link-metric col-12"><span class="app-public-review-generate__link-label">Locked Date</span> {{ formatTime(link.locked_at) }}</p>
                </template>

                <p class="app-public-review-generate__link-metric col-12">
                    <span class="app-public-review-generate__link-label">Issue Tracker</span>
                    <label :class="['element-toggle', !isIssutrackerLinked ? 'is--disabled' : '']">
                        <input 
                            type="checkbox"
                            :disabled="!isIssutrackerLinked"
                            :checked="isIssuetrackerEnabled(link)"
                            class="element-toggle__toggle"
                            @change="toggleIssuetracker(link)"
                        />
                        <span>{{ isIssuetrackerEnabled(link) ? 'On' : 'Off' }}</span>
                    </label>
                </p>

                <div v-if="enableSettings" :class="['app-publishing-dashboard__publishing-link-settings', 'app-accordion', isPreviewConfiguring[link.id] ? 'is--open' : '']">
                    <app-button class="app-accordion__header"
                        title="Custom Configuration" @click="
                        setPreviewConfiguring(link.id, !isPreviewConfiguring[link.id])">
                        <h4 class="app-accordion__title">Config Settings</h4>
                        <svg class="element-icon--chevron-down element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-chevron-down"></use></svg>
                    </app-button>

                    <div v-show="isPreviewConfiguring[link.id]" class="app-accordion__body">
                        <p class="element-instructions">Define config settings that will be applied to this specific review link.</p>
                        <code-editor name="previewconfiguration" :ref="`previewconfiguration-${link.id}`"
                            :value="getJSONString(link.configuration)"
                            :placeholder="'{\n\t&quot;key&quot;: &quot;value&quot;\n}'" language="json" rows="10"
                            @change="updatePreviewLinkConfiguration(link.id, $event)" class="d-block w-100">
                        </code-editor>
                        <p v-if="isLinkConfigSaving[link.id]">Saving...</p>
                    </div>
                </div>
            </div>
        </div>
        <template v-if="isLiteMode">
            <div class="app-public-review-generate__footer">
                <div v-if="!isIssutrackerLinked" class="inline-message">
                    <p>There isn’t an Issue Tracker currently linked to this project. This can be managed by Project Owners.</p>
                </div>
                <div class="app-public-review-generate__footer-buttons">
                    <button class="element-button element-button--secondary" @click="$emit('cancel')">Cancel</button>
                    <a v-if="isIssutrackerLinked" :href="issuetrackerUrl" target="_blank" class="element-button element-button--secondary">
                        <svg class="element-icon--open-new-window element-icon" width="1em" height="1em" aria-hidden="true"><use href="/img/icons.svg#icon-open-new-window"></use></svg>
                        View Tracker
                    </a>
                    <b-dropdown v-if="hasLocales" split @click="createLink(publicationData.id)" :text="isLinkCreating ? 'Creating...' : 'Create New Review Link'" class="element-button--secondary-dropdown-captions element-button--secondary-dropdown">
                        <b-dropdown-item v-for="(locale, index) in locales"
                            :key="`locale-${index}`"
                            @click="createLink(publicationData.id, locale.locale)"
                            >Create {{ locale.language_code }}  translation review link
                        </b-dropdown-item>
                    </b-dropdown>
                    <app-button v-else theme="primary" @click="createLink(publicationData.id)">
                        {{ isLinkCreating ? 'Creating...' : 'Create New Review Link' }}
                    </app-button>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import moment from 'moment'
import _ from 'lodash'
import Echo from '../helpers/echo'
import api from '../api/publisher'

export default {
    props: {
        publication: {
            type: Object,
        },
        project: {
            type: Object,
            default: () => ({}),
        },
        isLiteMode: {
            type: Boolean,
            default: false,
        },
        issuetrackerUrl: {
            type: String,
            default: null,
        },
        enableSettings: {
            type: Boolean,
            default: false,
        },
        enableTitle: {
            type: Boolean,
            default: false,
        },
    },
    data () {
        return {
            isLinkCreating: false,
            isIssutrackerLinked: this.project?.settings?.issuetracker ? true : false,
            publicationData: this.publication,
            isEditingName: {},
            isPreviewConfiguring: {},
            isLinkCopying: {},
            isLinkReseting: {},
            isLinkRemoving: {},
            isLinkNameSaving: {},
            isLinkConfigSaving: {},
        }
    },
    computed: {
        locales() {
            return this.project?.locales ?? []
        },
        hasLocales() {
            return this.locales.length > 0
        },
    },
    methods: {
        cancel () {
            this.$emit('cancel')
        },
        formatTime(ts) {
            return moment(ts).format('LLL')
        },
        getJSONString(obj) {
            if (!obj || (Array.isArray(obj) && obj.length === 0)) {
                return ''
            }
            return JSON.stringify(obj, null, 2)
        },
        getLanguageLabel(locale) {
            const item = this.locales.find(item => item.locale === locale);
            return item ? item.language_code : null;
        },
        isIssuetrackerEnabled(link) {
            return link?.configuration?.bugtracker?.enabled
        },
        async toggleIssuetracker(link) {
            const enabled = this.isIssuetrackerEnabled(link)

            let result = null
            if (enabled) {
                result = await api.issuetrackerUnlink(link.id)
            }
            else {
                result = await api.issuetrackerLink(link.id)
            }

            if (result && result?.publicLink && result?.publicLink?.configuration)
                link.configuration = result.publicLink.configuration
        },
        async createLink(id, locale = null) {
            this.isLinkCreating = true;
            const result = await api.createPublicLink(id, locale)

            if (result.errors) {
                console.error(result.errors)
            } else {
                this.updatePublication(id, { links: result })
            }
            this.isLinkCreating = false;
        },
        async resetLink(link) {
            if (!confirm('Are you sure you want to reset this link\'s expiration?')) {
                return
            }

            this.setLinkResetting(link, true);
            const result = await api.resetPublicLink(link);

            if (result.errors) {
                console.error(result.errors)
            } else {
                this.updatePublication(this.publicationData.id, { links: result })
            }
            this.setLinkResetting(link, false);
        },
        async removeLink(link) {
            if (!confirm('Are you sure you want to delete this link?')) {
                return
            }

            this.setLinkRemoving(link, true);
            const result = await api.deletePublicLink(link)
            if (result.errors) {
                console.error(result.errors)
            } else {
                this.updatePublication(this.publicationData.id, { links: result })
            }
            this.setLinkRemoving(link, false);
        },
        async copyLink(link, link_url) {
            link_url = link_url ?? link.url
            this.setLinkCopying(link.id, true);
            await navigator.clipboard.writeText(link_url);
            this.setLinkCopying(link.id, false);
        },
        async lockLink(link, lockedFlag) {
            if (lockedFlag == 0) {
                if (!confirm('Are you sure you want to lock this link?')) {
                    return
                }
            } else if (lockedFlag == 2) {
                if (!confirm('Are you sure you want to unlock this link?')) {
                    return
                }
            } else {
                return
            }

            const result = await api.lockPublicLink(link)
            if (!result || result.errors) {
                console.error(result?.errors || 'Lock/unlock failed')
            } else if (Array.isArray(result)) {
                this.updatePublication(this.publicationData.id, { links: result })
            }
        },
        async updatePreviewLinkName(id, event) {
            let inputEl = this.$refs['link-id-'+id][0]
            let linkName = inputEl.value
            if (_.isString(linkName) && !linkName.length) {
                linkName = ''
            }

            inputEl.value = 'Saving..'

            await this.publicPreviewNameUpdate({ id, linkName })
            this.isEditingName[id] = false;
        },
        async publicPreviewNameUpdate(link) {
            var data = {};
            data['id'] = link.id;
            data['name'] = link.linkName;

            const result = await api.publicPreviewLinkUpdate(link.id, data)

            if (result.errors) {
                console.error(result.errors)
            } else {
                Object.assign(this.publicationData, result)
            }

            this.setLinkNameSaving(link.id, false)
        },
        async publicPreviewConfigUpdate(link) {
            this.setLinkConfigSaving(link.id, true)

            // api call to save
            const result = await api.publicPreviewLinkUpdate(link.id, link)

            if (result.errors) {
                // catch errors
                this.setErrors(link.id, result)
            } else {
                // update publication
                console.log('updated link config', result)
                this.publicationData = result
                this.$emit('setupEcho', result)
            }

            this.setLinkConfigSaving(link.id, false)
        },
        async updatePreviewLinkConfiguration(id, event) {
            let configuration = event.target.value;
            if (_.isString(configuration) && !configuration.length) {
                configuration = null
            } else {
                configuration = JSON.parse(configuration)
            }

            // api call to save configuration
            await this.publicPreviewConfigUpdate({ id, configuration })
        },
        updatePublication (id, data) {
            this.publicationData.links = data.links;
        },
        setPreviewConfiguring(id, value = true) {
            this.isPreviewConfiguring[id] = value
        },
        setLinkCopying(id, value = true) {
            this.isLinkCopying[id] = value
        },
        setLinkResetting(id, value = true) {
            this.isLinkReseting[id] = value
        },
        setLinkRemoving(id, value = true) {
            this.isLinkRemoving[id] = value
        },
        setLinkNameSaving(id, value = true) {
            this.isLinkNameSaving[id] = value
        },
        setLinkConfigSaving(id, value = true) {
            this.isLinkConfigSaving[id] = value
        },
    },
    mounted() {
    },
    watch: {
        publication: {
            handler(newVal) {
                this.publicationData = _.merge({}, this.publicationData, newVal)
            },
            immediate: true,
        },
    }
}
</script>
